import type {
  ChatMessage,
  ToolResultBlock,
  ToolUseBlock,
} from '@/modules/chat/types'

export type ConfirmationState =
  | { status: 'pending' }
  | { status: 'applying' }
  | { status: 'applied' }
  | { status: 'not_applied'; reason: string | null }
  | { status: 'expired' }
  | { status: 'cancelled' }

export interface ApplyToolTarget {
  applyToolName: string
  // Wire-format key of the token inside the synthesized apply tool_use input
  // (e.g. 'token', 'batch_token'). An Apply all call lists them in `tokens`.
  tokenField: string
  token: string
}

interface LocalCardFlags {
  applied: boolean
  cancelled: boolean
  requested: boolean
  expired: boolean
}

interface ApplyResultPayload {
  token?: string
  applied?: boolean
  reason_code?: string
  reason?: string
  results?: ApplyResultPayload[]
  error?: { user_message?: string }
}

// The live stream only says a tool call finished, so the real result is read
// from the persisted session, which the client refetches when the turn ends.
export function findConfirmationOutcome(
  messages: ChatMessage[],
  target: ApplyToolTarget,
): ConfirmationState | null {
  const applyCall = lastApplyCall(messages, target)
  if (!applyCall) return null

  const result = findToolResult(messages, applyCall.id)
  if (!result) return { status: 'applying' }
  return outcomeFromResult(result, target.token)
}

export function resolveConfirmationState(
  outcome: ConfirmationState | null,
  { applied, cancelled, requested, expired }: LocalCardFlags,
): ConfirmationState {
  if (outcome) return outcome
  if (applied) return { status: 'applied' }
  if (cancelled) return { status: 'cancelled' }
  if (requested) return { status: 'applying' }
  if (expired) return { status: 'expired' }
  return { status: 'pending' }
}

function lastApplyCall(
  messages: ChatMessage[],
  target: ApplyToolTarget,
): ToolUseBlock | null {
  let found: ToolUseBlock | null = null
  for (const message of messages) {
    if (message.role !== 'assistant') continue
    for (const block of message.contentJson) {
      if (block.type !== 'tool_use' || block.name !== target.applyToolName) {
        continue
      }
      if (inputConfirmsToken(block.input, target)) found = block
    }
  }
  return found
}

function inputConfirmsToken(input: unknown, target: ApplyToolTarget): boolean {
  const fields = input as Record<string, unknown> | null
  if (!fields) return false
  if (fields[target.tokenField] === target.token) return true
  return Array.isArray(fields.tokens) && fields.tokens.includes(target.token)
}

function findToolResult(
  messages: ChatMessage[],
  toolUseId: string,
): ToolResultBlock | null {
  for (const message of messages) {
    for (const block of message.contentJson) {
      if (block.type === 'tool_result' && block.tool_use_id === toolUseId) {
        return block
      }
    }
  }
  return null
}

function outcomeFromResult(
  block: ToolResultBlock,
  token: string,
): ConfirmationState {
  const payload = parsePayload(block.content)
  if (block.is_error) {
    return {
      status: 'not_applied',
      reason: payload?.error?.user_message ?? null,
    }
  }
  if (!payload) return { status: 'applied' }
  const tokenResult = payload.results
    ? payload.results.find((result) => result.token === token)
    : payload
  if (!tokenResult) return { status: 'not_applied', reason: null }
  if (tokenResult.applied !== false) return { status: 'applied' }
  // Another viewer, or an earlier Apply all, already applied it.
  if (tokenResult.reason_code === 'already_applied') {
    return { status: 'applied' }
  }
  if (tokenResult.reason_code === 'expired') return { status: 'expired' }
  if (tokenResult.reason_code === 'cancelled') return { status: 'cancelled' }
  return { status: 'not_applied', reason: tokenResult.reason ?? null }
}

function parsePayload(content: string): ApplyResultPayload | null {
  try {
    return JSON.parse(content) as ApplyResultPayload
  } catch {
    return null
  }
}
